'use client'

import type { TextClampProps } from '@pearlconvert/ui/components/text-clamp/text-clamp.types'

import classNames from 'classnames'
import { useTranslations } from 'next-intl'
import { useMemo } from 'react'

import { Button } from '@pearlconvert/ui/components/button'
import { useTextClamp } from '@pearlconvert/ui/components/text-clamp/batons/use-text-clamp'

import '@pearlconvert/ui/components/text-clamp/styles/text-clamp.scss'

export default function TextClamp({
  children,
  collapseAriaLabel,
  collapseText,
  expandAriaLabel,
  expandText,
  initiallyExpanded = false,
  onToggle,
}: TextClampProps) {
  const t = useTranslations('ui')
  const { textRef, isExpanded, isOverflowing, toggleExpand } = useTextClamp({
    initiallyExpanded,
    onToggle,
  })

  const toggleButton = useMemo(() => {
    return isOverflowing ? (
      <Button
        type="link"
        className="text-clamp__button"
        onClick={toggleExpand}
        title={isExpanded ? collapseAriaLabel : expandAriaLabel}
      >
        {isExpanded
          ? collapseText || t('_globals.show-less')
          : expandText || t('_globals.show-more')}
      </Button>
    ) : null
  }, [
    collapseAriaLabel,
    collapseText,
    expandAriaLabel,
    expandText,
    isExpanded,
    isOverflowing,
    t,
    toggleExpand,
  ])

  return (
    <div className="text-clamp__container">
      <div
        ref={textRef}
        className={classNames('text-clamp__text', {
          'text-clamp__text--collapsed': !isExpanded && isOverflowing,
          'text-clamp__text--expanded': isExpanded,
        })}
      >
        {children}
      </div>
      {toggleButton}
    </div>
  )
}
